<script setup>
import Layout from "../../../layouts/main.vue";
import { onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import MethodService from "../../../service/MethodService";
import mushroom from "cem-primary-api";
import Swal from "sweetalert2";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
import { getDashboardOptionsWithMenuLabel } from "./searchLinkService";
import Data from "./dataFormSearchLink";
import { Project, Filter } from "mushroomjs";
const { t } = useI18n();
let loading = ref(false);
const route = useRoute();

// Lấy search_strategy_id từ URL
const searchStrategyId = computed(() => route.query?.search_strategy_id || "");

// Tên hướng tìm kiếm
const currentStrategyName = ref("");

// Dữ liệu động
const filterColumns = ref([]); // các cột từ search_filter
const dashboardOptions = ref([]); // options cho cột Link
const rows = ref([]); // danh sách rows hiển thị trong bảng
const rowErrors = ref([]); // lỗi validate từng row

// Lấy tên hướng tìm kiếm từ API
const getStrategyName = async () => {
  const id = searchStrategyId.value;
  if (!id) {
    currentStrategyName.value = "";
    return;
  }
  try {
    const response = await mushroom.search_strategy.findByIdAsync({
      id: id,
      fields: Project.include("id", "name").build(),
    });
    if (response.result) {
      currentStrategyName.value =
        response.result.name?.vi || response.result.name?.en || "";
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

// Lấy danh sách filter → tạo cột động
const getFilterColumns = async () => {
  const id = searchStrategyId.value;
  if (!id) {
    filterColumns.value = [];
    return;
  }
  mushroom.search_filter.invalidCache();
  try {
    const response = await mushroom.search_filter.getAllAsync({
      fields: Project.include("id", "name", "code").build(),
      filters: Filter.eq("search_strategy_id", id).build(),
    });
    if (response.result) {
      filterColumns.value = response.result.map((item) => ({
        id: item.id,
        label: item.name?.vi || item.name?.en || item.code || item.id,
        code: item.code,
      }));
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

// Lấy danh sách dashboard → options cho cột Link
const getDashboardOptions = async () => {
  dashboardOptions.value = await getDashboardOptionsWithMenuLabel();
};

// Options cho mỗi cell filter (Mặc định / User)
const combinationTypeOptions = Data.combinationTypeOptions;
// Options cho cột Loại (support_type: Tổng hợp / Chi tiết)
const supportTypeOptions = Data.supportTypeOptions;

// Lấy danh sách search_link theo search_strategy_id
const getListSearchLink = async () => {
  loading.value = true;
  const currentStrategyId = searchStrategyId.value;
  if (!currentStrategyId) {
    rows.value = [];
    loading.value = false;
    return;
  }
  try {
    mushroom.search_link.invalidCache();
    const response = await mushroom.search_link.getAllAsync({
      filters: Filter.eq("search_strategy_id", currentStrategyId).build(),
      fields: Project.include(
        "id",
        "search_strategy_id",
        "combination",
        "link",
      ).build(),
    });
    if (response.result) {
      rows.value = response.result.map((item) => buildRowFromData(item));
    } else {
      rows.value = [];
    }
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

// Chuyển data từ API thành row hiển thị
const buildRowFromData = (item) => {
  const row = {
    id: item.id,
    link_dashboard_id: item.link?.dynamic_dashboard_id || undefined,
    search_strategy_id: item.search_strategy_id,
    support_type: undefined,
    filters: {},
  };
  if (item.combination && item.combination.length > 0) {
    item.combination.forEach((combo) => {
      if (combo.search_filter_id) {
        row.filters[combo.search_filter_id] = combo.type || "default";
      }
      // support_type là giá trị chung của row cùng giá trị, lấy từ combination đầu tiên có giá trị
      if (!row.support_type && combo.support_type) {
        row.support_type = combo.support_type;
      }
    });
  }
  return row;
};

// Xóa tất cả lỗi validate khi người dùng thay đổi input
const clearRowError = () => {
  rowErrors.value = [];
};
const addNewRow = () => {
  const newRow = {
    id: null,
    link_dashboard_id: undefined,
    support_type: undefined,
    filters: {},
  };
  rows.value.push(newRow);
  rowErrors.value = []; // Xóa lỗi cũ khi thêm row
};

// Xóa row
const removeRow = (index) => {
  const row = rows.value[index];
  if (row.id) {
    Swal.fire({
      title: t("t_warning"),
      text: t("t_record_delete"),
      icon: "warning",
      confirmButtonColor: "#f46a6a",
      confirmButtonText: t("swal_accept"),
      cancelButtonText: t("swal_cancel"),
      showCancelButton: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await mushroom.search_link.deleteAsync({
            id: row.id,
          });
          if (response.result) {
            toastr.success(t("toastr_delete_success"));
            rows.value.splice(index, 1);
          }
        } catch (e) {
          console.error("Có lỗi: %o", e);
          MethodService.showError(e.code);
        }
      }
    });
  } else {
    rows.value.splice(index, 1);
  }
};

// Lưu 1 row (tạo mới hoặc cập nhật)
const saveRow = async (index) => {
  const row = rows.value[index];
  const currentStrategyId = searchStrategyId.value;
  if (!currentStrategyId) {
    toastr.error(t("t-place-search-link-strategy"));
    return false;
  }
  if (!row.support_type) {
    toastr.error(t("t-place-search-strategy-support-type"));
    return false;
  }
  if (!row.link_dashboard_id) {
    toastr.error(t("t-place-search-link-dashboard"));
    return false;
  }

  const combination = [];
  filterColumns.value.forEach((col) => {
    const type = row.filters[col.id];
    if (type) {
      combination.push({
        support_type: row.support_type,
        search_filter_id: col.id,
        type: type,
      });
    }
  });

  // combination phải có ít nhất 1 phần tử
  if (combination.length === 0) {
    toastr.error(t("t-search-link-combination-required"));
    return false;
  }

  const objectData = {
    search_strategy_id: currentStrategyId,
    combination: combination,
    link: {
      dynamic_dashboard_id: row.link_dashboard_id,
    },
  };
  try {
    if (row.id) {
      // Cập nhật
      objectData.id = row.id;
      await mushroom.search_link.partialUpdateAsync(objectData);
    } else {
      // Tạo mới
      const {result} = await mushroom.search_link.createAsync(objectData);
      if (result) {
        rows.value[index].id = result;
      }
    }
    toastr.success(t("toastr_update_success"));
    return true;
  } catch (error) {
    if (error?.meta?.field === "$unique_key") {
      toastr.error(t("toastr_name_existed"));
    } else {
      console.error("Có lỗi: %o", error);
      MethodService.showError(error.code);
    }
    return false;
  }
};

// Lưu tất cả rows
const saveAll = async () => {
  const currentStrategyId = searchStrategyId.value;
  if (!currentStrategyId) {
    toastr.error(t("t-place-search-link-strategy"));
    return;
  }

  // Validate từng row — gán lỗi vào rowErrors
  rowErrors.value = [];
  let hasError = false;

  for (let i = 0; i < rows.value.length; i++) {
    const row = rows.value[i];
    const hasCombination = filterColumns.value.some(
      (col) => row.filters[col.id],
    );
    const hasLink = !!row.link_dashboard_id;
    const hasSupportType = !!row.support_type;

    if (!hasCombination && !hasLink) {
      rowErrors.value[i] = t("t-search-link-error-both");
      hasError = true;
    } else if (!hasCombination) {
      rowErrors.value[i] = t("t-search-link-combination-required");
      hasError = true;
    } else if (!hasSupportType) {
      rowErrors.value[i] = t("t-place-search-strategy-support-type");
      hasError = true;
    } else if (!hasLink) {
      rowErrors.value[i] = t("t-search-link-error-link");
      hasError = true;
    } else {
      rowErrors.value[i] = "";
    }
  }

  if (hasError) return;

  // Validate trùng combination (tính cả support_type, ưu tiên support_type trước)
  const getCombinationKey = (row) => {
    const filterKey = filterColumns.value
      .filter((col) => row.filters[col.id])
      .map((col) => `${col.id}:${row.filters[col.id]}`)
      .join("|");
    return `support_type:${row.support_type || ""}|${filterKey}`;
  };

  const seen = {};
  for (let i = 0; i < rows.value.length; i++) {
    const key = getCombinationKey(rows.value[i]);
    if (seen[key] !== undefined) {
      rowErrors.value[i] = t("t-search-link-error-duplicate-combination");
      rowErrors.value[seen[key]] = t(
        "t-search-link-error-duplicate-combination",
      );
      hasError = true;
    } else {
      seen[key] = i;
    }
  }

  if (hasError) return;

  // Lưu lần lượt
  for (let i = 0; i < rows.value.length; i++) {
    const success = await saveRow(i);
    if (!success) return;
  }
};

onMounted(async () => {
  await getStrategyName();
  await getDashboardOptions();
  await getFilterColumns();
  await getListSearchLink();
});
</script>

<template>
  <Layout>
    <div class="card" style="min-height: 80vh">
      <div class="card-header">
        <h3 class="text-uppercase mb-0">
          {{ $t("t-config-search-link") }}
          <span v-if="currentStrategyName"> {{ currentStrategyName }}</span>
        </h3>
        <div class="text-end">
          <div class="btn-header">
            <button class="btn btn-secondary btn-border" @click="saveAll">
              <i class="ri-save-line"></i>
              {{ $t("t_save") }}
            </button>
          </div>
        </div>
      </div>
      <div class="card-body" style="padding: 0 0 15px 0">
        <div class="main-body">
          <!-- Bảng cấu hình động -->
          <div class="config-table-wrapper mx-3 mt-3" v-loading="loading">
            <div class="config-table-scroll">
              <table class="config-table">
                <thead>
                  <tr>
                    <!-- Cột động từ search_filter -->
                    <th
                      v-for="col in filterColumns"
                      :key="col.id"
                      class="config-col-dynamic"
                    >
                      {{ col.label }}
                    </th>
                    <!-- Cột Loại (support_type) -->
                    <th class="config-col-support-type">
                      {{ $t("t-search-strategy-support-type") }}
                    </th>
                    <!-- Cột cố định: Link -->
                    <th class="config-col-fixed config-col-link">Link</th>
                    <!-- Cột cố định: Thao tác -->
                    <th class="config-col-fixed config-col-action">
                      {{ $t("t-action") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(row, rowIndex) in rows" :key="rowIndex">
                    <tr>
                      <td
                        v-for="col in filterColumns"
                        :key="col.id"
                        class="config-col-dynamic"
                      >
                        <el-select
                          v-model="row.filters[col.id]"
                          clearable
                          size="small"
                          :placeholder="$t('t-place-search-link-type')"
                          style="width: 100%"
                          @change="clearRowError()"
                        >
                          <el-option
                            v-for="opt in combinationTypeOptions"
                            :key="opt.value"
                            :label="$t(opt.labelKey)"
                            :value="opt.value"
                          />
                        </el-select>
                      </td>
                      <td class="config-col-support-type">
                        <el-select
                          v-model="row.support_type"
                          clearable
                          size="small"
                          :placeholder="
                            $t('t-place-search-strategy-support-type')
                          "
                          style="width: 100%"
                          @change="clearRowError()"
                        >
                          <el-option
                            v-for="opt in supportTypeOptions"
                            :key="opt.value"
                            :label="$t(opt.labelKey)"
                            :value="opt.value"
                          />
                        </el-select>
                      </td>
                      <td class="config-col-fixed config-col-link">
                        <el-select
                          v-model="row.link_dashboard_id"
                          filterable
                          size="small"
                          :placeholder="$t('t-place-search-link-dashboard')"
                          style="width: 100%"
                          :no-match-text="$t('t-match-data')"
                          :no-data-text="$t('t-no-data')"
                          @change="clearRowError()"
                        >
                          <el-option
                            v-for="item in dashboardOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                          />
                        </el-select>
                      </td>
                      <td class="config-col-fixed config-col-action">
                        <div class="btn-group-thao-tac">
                          <el-tooltip
                            :content="$t('t-delete')"
                            placement="top"
                            :enterable="false"
                            effect="light"
                          >
                            <button
                              type="button"
                              class="btn btn-soft-danger waves-effect waves-light btn-sm"
                              @click="removeRow(rowIndex)"
                            >
                              <i class="ri-delete-bin-6-line"></i>
                            </button>
                          </el-tooltip>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="rowErrors[rowIndex]" class="row-error-tr">
                      <td
                        :colspan="filterColumns.length + 3"
                        class="row-error-td"
                      >
                        <span class="row-error-msg">{{
                          rowErrors[rowIndex]
                        }}</span>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="rows.length === 0 && !loading">
                    <td
                      :colspan="filterColumns.length + 3"
                      class="text-center py-4 text-muted"
                    >
                      {{ $t("t-no-data") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="m-2">
              <button
                type="button"
                class="btn btn-soft-secondary btn-border btn-sm"
                @click="addNewRow"
              >
                <i class="ri-add-circle-line"></i>
                {{ $t("t-search-link-add-combination") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.config-table-wrapper {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.config-table-scroll {
  overflow-x: auto;
  width: 100%;
}

.config-table {
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  table-layout: auto;
}

.config-table thead th {
  background: #f5f7fa;
  padding: 12px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  border-bottom: 1px solid #e4e7ed;
  white-space: nowrap;
  text-align: center;
}

.config-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #ebeef5;
  vertical-align: middle;
}

.config-col-dynamic {
  min-width: 140px;
}

.config-col-support-type {
  min-width: 140px;
}

/* Cột cố định Link và Thao tác */
.config-col-fixed {
  position: sticky;
  background: #fff;
  z-index: 2;
}

.config-table thead .config-col-fixed {
  background: #f5f7fa;
  z-index: 3;
}

.config-col-link {
  right: 80px;
  min-width: 200px;
  border-left: 1px solid #e4e7ed;
}

.config-col-action {
  right: 0;
  min-width: 80px;
  width: 80px;
  text-align: center;
  border-left: 1px solid #e4e7ed;
}

.btn-group-thao-tac {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.row-error-tr {
  background: transparent;
}

.row-error-td {
  padding: 2px 10px 8px 10px !important;
  border-bottom: 1px solid #ebeef5;
}

.row-error-msg {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.2;
}
</style>
